import { useExperimentContext } from "components/ExperimentProvider/hook";
import { useUserContext } from "components/UserProvider";
import type { CMSPageExperiment } from "lib/cms/types/experiments";
import { useRouter } from "lib/router";
import { useMemo, useRef } from "react";
import type { SkinnyBanner } from "@shared/graphql.types";
import { showSkinnyBanner } from "./utils";

const getSelectedBannerVariant = (
  skinnyBanner: SkinnyBanner,
  selectedVariantId?: string | number | boolean
): {
  selectedVariantIndex: number;
  selectedBannerVariant: SkinnyBanner;
} => {
  const allBannerVariants = [skinnyBanner, ...(skinnyBanner?.variants ?? [])];

  if (!selectedVariantId || typeof selectedVariantId !== "string") {
    return { selectedVariantIndex: 0, selectedBannerVariant: skinnyBanner };
  }

  let selectedVariantIndex = Number.parseInt(selectedVariantId, 10);
  if (Number.isNaN(selectedVariantIndex)) {
    selectedVariantIndex = 0;
  }

  const selectedBannerVariant = allBannerVariants[selectedVariantIndex];

  // variants[selectedVariantIndex] doesn't exist because there are more LaunchDarkly variants than CMS variants
  if (!selectedBannerVariant) {
    return { selectedVariantIndex: 0, selectedBannerVariant: skinnyBanner };
  }

  return {
    selectedVariantIndex,
    selectedBannerVariant
  };
};

export const useSkinnyBannerItem = (
  items: SkinnyBanner[]
): (SkinnyBanner & CMSPageExperiment) | null => {
  const { asPath } = useRouter();
  const { isMember } = useUserContext();
  const expirationDate = useRef(new Date());

  const { getValue } = useExperimentContext();

  return useMemo(() => {
    let mostSpecificBanner: SkinnyBanner | null = null;
    let highestSpecificity = -1;

    for (const skinnyBannerItem of items) {
      expirationDate.current = skinnyBannerItem?.countdown?.countdownEndDate;

      const isDisabledForMembers = isMember && !skinnyBannerItem?.isMember;
      const isDisabledForNonMembers =
        !isMember && !skinnyBannerItem?.isNonMember;

      if (isDisabledForMembers || isDisabledForNonMembers) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const filterNulls = (arr: (string | null)[] = []): string[] =>
        arr.filter((item): item is string => item !== null);

      const pagesToShow = filterNulls(skinnyBannerItem?.pagesToShow ?? []);
      const pagesToExclude = filterNulls(
        skinnyBannerItem?.pagesToExclude ?? []
      );

      const bannerSpecificity = showSkinnyBanner(
        asPath,
        pagesToShow,
        pagesToExclude
      );

      if (bannerSpecificity > highestSpecificity) {
        highestSpecificity = bannerSpecificity;
        mostSpecificBanner = skinnyBannerItem;
      }
    }

    if (
      !mostSpecificBanner?.experimentId ||
      !mostSpecificBanner?.variants?.length
    ) {
      // most specific skinny banner is not part of an experiment
      return mostSpecificBanner;
    }

    // most specific skinny banner is part of an experiment
    // retrive selected variant
    const selectedVariantId = getValue(mostSpecificBanner?.experimentId);

    const { selectedBannerVariant, selectedVariantIndex } =
      getSelectedBannerVariant(mostSpecificBanner, selectedVariantId);

    return {
      ...selectedBannerVariant,
      selectedVariantIndex,
      experimentId: mostSpecificBanner.experimentId
    };
  }, [items, isMember, asPath, getValue]);
};
